import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { Category } from '@/domain/impact-co2/entities/question.entity';
import { selectImpactCo2AnswersImportedForCategories } from '@/domain/impact-co2/impact-co2.selectors';
import { selectCurrentProject } from '@/domain/project/project.selectors';
import { FSToggleButtonGroup } from '@/ui/components/button/FSToggleButtonGroup';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';

import { Co2QuizExternalAnswers } from './Co2QuizExternalAnswers';
import { Co2QuizFormData } from './Co2QuizFormData';
import { Co2QuizImportedAnswers } from './Co2QuizImportedAnswers';
import { Co2QuizMyAnswers } from './Co2QuizMyAnswers';
import { Co2QuizPublicAnswers } from './Co2QuizPublicAnswers';

export function Co2Quiz({
  form,
  externalForm,
  enableExternalPublic = false,
  enableExternalAnswsersView = true,
  enableImportedAnswersView = false,
}: {
  form?: Co2QuizFormData;
  externalForm?: Co2QuizFormData;
  enableExternalPublic: boolean;
  enableExternalAnswsersView: boolean;
  enableImportedAnswersView: boolean;
}) {
  const categoriesToShow = getCategoriesToShow(form);
  const importedAnswersToShow = useAppSelector(
    selectImpactCo2AnswersImportedForCategories({
      sector: form?.sector || '',
      categories: categoriesToShow,
    }),
  );
  const hasImportedAnswersToShow = importedAnswersToShow.length > 0;

  const [currentTab, setCurrentTab] = useState<TabId>('my-answers');
  const tabs = buildTabs({
    enableExternalPublic,
    enableExternalAnswsersView,
    enableImportedAnswersView,
    hasImportedAnswersToShow,
  });
  const hideTabs = tabs.length <= 1;

  useEffect(() => {
    const selectFirstTabWhenFormChange = () => {
      if (tabs.length > 0) {
        setCurrentTab(tabs[0].id);
      }
    };
    selectFirstTabWhenFormChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const project = useAppSelector(selectCurrentProject);
  if (!project || !form) {
    return null;
  }

  return (
    <>
      {!hideTabs && (
        <TabsView
          tabs={tabs}
          currentTab={currentTab}
          onChangeTab={setCurrentTab}
        />
      )}
      {currentTab === 'my-answers' && (
        <Co2QuizMyAnswers projectId={project.id} form={form} />
      )}
      {currentTab === 'external-answers' && (
        <Co2QuizExternalAnswers projectId={project.id} form={externalForm} />
      )}
      {currentTab === 'public-answers' && <Co2QuizPublicAnswers />}
      {currentTab === 'imported-answers' && (
        <Co2QuizImportedAnswers projectId={project.id} form={form} />
      )}
    </>
  );
}

function getCategoriesToShow(form?: Co2QuizFormData): Category[] {
  if (!form) return [];
  return form.sections.flatMap((section) => section.questions);
}

type Tabs = { id: TabId; title: string }[];
type TabId =
  | 'my-answers'
  | 'external-answers'
  | 'public-answers'
  | 'imported-answers';

/** Generate tabs to show depending on view to display */
function buildTabs({
  enableExternalPublic,
  enableExternalAnswsersView,
  enableImportedAnswersView,
  hasImportedAnswersToShow,
}: {
  enableExternalPublic: boolean;
  enableExternalAnswsersView: boolean;
  enableImportedAnswersView: boolean;
  hasImportedAnswersToShow: boolean;
}): Tabs {
  const tabs: Tabs = [
    {
      id: 'my-answers',
      title: translate('Quiz.my-answers-organisator'),
    },
  ];
  if (enableExternalAnswsersView) {
    tabs.push({
      id: 'external-answers',
      title: translate('Quiz.answers-of-externals'),
    });
  }
  if (enableExternalPublic) {
    tabs.push({
      id: 'public-answers',
      title: translate('Quiz.answers-of-my-publics'),
    });
  }
  if (enableImportedAnswersView && hasImportedAnswersToShow) {
    tabs.push({
      id: 'imported-answers',
      title: translate('Quiz.answers-imported'),
    });
  }
  return tabs;
}

function TabsView({
  tabs,
  currentTab,
  onChangeTab,
}: {
  tabs: Tabs;
  currentTab: TabId;
  onChangeTab: (tab: TabId) => void;
}) {
  const values = tabs.map((tab) => ({
    id: tab.id,
    value: <Typography>{tab.title}</Typography>,
  }));
  return (
    <Box sx={{ py: 2, textAlign: 'center' }}>
      <FSToggleButtonGroup
        selectedValueId={currentTab}
        values={values}
        onChange={(value) => onChangeTab(value as TabId)}
      />
    </Box>
  );
}
